import type { ReactNode } from "react";
import { cn } from "../../lib/cn";
import { AltArrowRightLinearIcon } from "../../icons";
import { Skeleton } from "../Skeleton";

// The back arrow needs an accessible name, so a label is required with it.
type PageHeaderBackProps =
  | { readonly onBack: () => void; readonly backLabel: string }
  | { readonly onBack?: undefined; readonly backLabel?: undefined };

export type PageHeaderProps = PageHeaderBackProps & {
  readonly title: ReactNode;
  readonly subtitle?: ReactNode;
  /** Status badge shown next to the title (detail screens). */
  readonly badge?: ReactNode;
  /** End-side actions: Edit, row menu, ... (detail screens). */
  readonly actions?: ReactNode;
  /** Keeps `actions` on the title's row on mobile too, instead of stacking
   * them underneath -- for a lone compact action such as an icon button. */
  readonly inlineActions?: boolean;
  /** Rows under the title that stay in view with it, e.g. a list's search
   * and buttons. */
  readonly children?: ReactNode;
  /** Stays at the top while the page scrolls. Defaults to true. */
  readonly sticky?: boolean;
  readonly className?: string | undefined;
};

// The negative offsets cancel the page's `p-3 md:p-4`, so the sticky bar
// spans edge to edge and hides content scrolling under it.
const stickyClassName =
  "sticky top-0 z-20 -mx-3 -mt-3 bg-container-default-background px-3 pt-3 pb-2 md:-mx-4 md:-mt-4 md:px-4 md:pt-4";

/** Page title block shared by list, detail and form screens: optional back
 * arrow, title (+ badge), subtitle, end-side actions and extra toolbar rows.
 * Expects the page to use `p-3 md:p-4`. */
export function PageHeader({
  title,
  subtitle,
  badge,
  actions,
  inlineActions = false,
  children,
  onBack,
  backLabel,
  sticky = true,
  className,
}: PageHeaderProps) {
  return (
    <div
      className={cn(
        "flex flex-col gap-4",
        sticky && stickyClassName,
        className,
      )}
    >
      <header
        className={cn(
          "flex md:flex-row md:items-start md:justify-between md:gap-4",
          inlineActions
            ? "flex-row items-start justify-between gap-3"
            : "flex-col items-stretch gap-3",
        )}
      >
        <div className="flex min-w-0 items-start gap-1.5">
          {onBack ? (
            <button
              type="button"
              aria-label={backLabel}
              onClick={onBack}
              className="inline-flex size-8 shrink-0 items-center justify-center rounded-md text-secondary-icon transition-colors hover:bg-container-hover hover:text-text focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
            >
              <AltArrowRightLinearIcon
                className="size-4 rotate-180 rtl:rotate-0"
                aria-hidden="true"
              />
            </button>
          ) : null}
          <div className="min-w-0 space-y-1">
            <div className="flex min-w-0 items-center gap-2">
              <h1 className="min-w-0 truncate text-heading-h5-semibold text-text">
                {title}
              </h1>
              {badge}
            </div>
            {subtitle ? (
              <p className="text-body-m-r text-text-placeholder">{subtitle}</p>
            ) : null}
          </div>
        </div>
        {actions ? (
          <div
            className={cn(
              "flex flex-wrap items-center gap-2",
              inlineActions && "shrink-0",
            )}
          >
            {actions}
          </div>
        ) : null}
      </header>
      {children}
    </div>
  );
}

/** Loading stand-in shaped like `PageHeader`, so the title doesn't jump when
 * the data arrives. `actions` are Tailwind width classes so callers can
 * roughly match their real button widths. */
export function PageHeaderSkeleton({
  withBack = false,
  withBadge = false,
  subtitle = false,
  actions = ["w-24"],
}: {
  readonly withBack?: boolean;
  readonly withBadge?: boolean;
  readonly subtitle?: boolean;
  readonly actions?: readonly string[];
}) {
  return (
    <div className="flex flex-col items-stretch gap-3 md:flex-row md:flex-wrap md:items-start md:justify-between md:gap-4">
      <div className="flex items-start gap-1.5">
        {withBack ? <Skeleton className="size-8 shrink-0 rounded-md" /> : null}
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <Skeleton className="h-6 w-44 rounded-full" />
            {withBadge ? <Skeleton className="h-6 w-16 rounded-md" /> : null}
          </div>
          {subtitle ? <Skeleton className="h-4 w-56 rounded-full" /> : null}
        </div>
      </div>
      <div className="flex items-center gap-3">
        {actions.map((widthClass, index) => (
          <Skeleton
            key={index}
            // Callers pass a desktop width to match their real button; on mobile
            // the buttons go full-width, so the stand-in has to as well.
            className={`h-9 flex-1 rounded-md md:flex-none ${widthClass}`}
          />
        ))}
      </div>
    </div>
  );
}
